import React from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Heart,
  PlusCircle,
  RefreshCw,
  Edit3,
  Trash2,
  X,
  Image as ImageIcon,
} from "lucide-react";
import Button from "../../../../components/common/Button";
import { Input } from "../../../../components/common/Input";
import { CoreValueItem } from "./types";

export interface CoreValuesTabProps {
  coreValuesList: CoreValueItem[];
  loadingValues: boolean;
  valueModalOpen: boolean;
  setValueModalOpen: (open: boolean) => void;
  editingValueId: number | null;
  valueTitle: string;
  setValueTitle: (title: string) => void;
  valueDescription: string;
  setValueDescription: (desc: string) => void;
  valuePicFile: File | null;
  setValuePicFile: (file: File | null) => void;
  valuePicUrl: string;
  setValuePicUrl: (url: string) => void;
  setValuePicMediaId?: (id: number | string | null) => void;
  savingValue: boolean;
  handleOpenAddValue: () => void;
  handleOpenEditValue: (item: CoreValueItem) => void;
  handleSaveCoreValue: (e: React.FormEvent) => void;
  handleDeleteCoreValue: (id: number, name?: string) => void;
  onOpenMediaModalForCoreValue: () => void;
}

export const CoreValuesTab: React.FC<CoreValuesTabProps> = ({
  coreValuesList,
  loadingValues,
  valueModalOpen,
  setValueModalOpen,
  editingValueId,
  valueTitle,
  setValueTitle,
  valueDescription,
  setValueDescription,
  valuePicFile,
  setValuePicFile,
  valuePicUrl,
  setValuePicUrl,
  setValuePicMediaId,
  savingValue,
  handleOpenAddValue,
  handleOpenEditValue,
  handleSaveCoreValue,
  handleDeleteCoreValue,
  onOpenMediaModalForCoreValue,
}) => {
  return (
    <div className="cht-space-y-6">
      <div className="cht-flex cht-items-center cht-justify-between">
        <div>
          <h3 className="cht-text-lg cht-font-black cht-text-[#1e293b]">Company Core Values</h3>
          <p className="cht-text-xs cht-text-gray-500">Manage foundational principles and culture pillars displayed across public web pages.</p>
        </div>
        <Button
          variant="primary"
          size="sm"
          onClick={handleOpenAddValue}
          leftIcon={<PlusCircle className="cht-h-4 cht-w-4" />}
          className="cht-rounded-xl cht-font-bold cht-text-xs"
        >
          Add Core Value
        </Button>
      </div>

      <div className="cht-bg-white cht-border cht-border-gray-100 cht-shadow-sm cht-rounded-[24px] cht-p-6">
        {loadingValues ? (
          <div className="cht-py-12 cht-text-center cht-text-xs cht-text-gray-500">
            <RefreshCw className="cht-h-5 cht-w-5 cht-text-[#3e57da] cht-animate-spin cht-mx-auto cht-mb-2" />
            Loading company core values...
          </div>
        ) : coreValuesList.length > 0 ? (
          <div className="cht-grid cht-grid-cols-1 md:cht-grid-cols-2 lg:cht-grid-cols-3 cht-gap-4">
            {coreValuesList.map((val) => {
              const valImg = val.pic || val.pic_url || (val as any).picture || null;
              return (
                <div
                  key={val.id}
                  className="cht-p-5 cht-bg-gray-50/80 cht-border cht-border-gray-100 cht-rounded-2xl cht-space-y-3 hover:cht-shadow-md cht-transition-all"
                >
                  <div className="cht-flex cht-items-center cht-justify-between">
                    <div className="cht-h-12 cht-w-12 cht-rounded-xl cht-bg-emerald-50 cht-border cht-border-emerald-100 cht-overflow-hidden cht-flex cht-items-center cht-justify-center cht-shrink-0">
                      {valImg ? (
                        <img src={valImg} alt={val.title} className="cht-w-full cht-h-full cht-object-cover" />
                      ) : (
                        <Heart className="cht-h-6 cht-w-6 cht-text-emerald-600" />
                      )}
                    </div>
                    <div className="cht-flex cht-items-center cht-space-x-1">
                      <button
                        type="button"
                        onClick={() => handleOpenEditValue(val)}
                        className="cht-h-8 cht-w-8 cht-rounded-lg cht-flex cht-items-center cht-justify-center cht-text-gray-500 hover:cht-text-[#3e57da] hover:cht-bg-indigo-50/60 cht-transition-colors"
                        title="Edit Core Value"
                      >
                        <Edit3 className="cht-h-4 cht-w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteCoreValue(val.id, val.title)}
                        className="cht-h-8 cht-w-8 cht-rounded-lg cht-flex cht-items-center cht-justify-center cht-text-gray-500 hover:cht-text-rose-600 hover:cht-bg-rose-50/60 cht-transition-colors"
                        title="Delete Core Value"
                      >
                        <Trash2 className="cht-h-4 cht-w-4" />
                      </button>
                    </div>
                  </div>
                  <div className="cht-space-y-1">
                    <h4 className="cht-font-black cht-text-[#1e293b] cht-text-sm">{val.title}</h4>
                    <p className="cht-text-xs cht-text-gray-600 cht-leading-relaxed">{val.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="cht-py-12 cht-text-center cht-text-xs cht-text-gray-500">
            <Heart className="cht-h-8 cht-w-8 cht-text-gray-300 cht-mx-auto cht-mb-2" />
            <p className="cht-font-bold cht-text-[#1e293b]">No Core Values Defined</p>
            <p className="cht-text-gray-400 cht-mt-1">Click "Add Core Value" above to publish corporate principles.</p>
          </div>
        )}
      </div>

      {/* CORE VALUE MODAL */}
      {valueModalOpen &&
        createPortal(
          <AnimatePresence>
            <div className="cht-fixed cht-inset-0 cht-w-screen cht-h-screen cht-min-h-screen cht-bg-slate-900/60 cht-backdrop-blur-xs cht-z-[99999] cht-flex cht-items-center cht-justify-center cht-p-4 cht-overflow-y-auto">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                className="cht-bg-white cht-rounded-[24px] cht-p-6 cht-max-w-md cht-w-full cht-shadow-2xl cht-space-y-4 cht-border cht-border-gray-100 cht-my-auto"
              >
                <div className="cht-flex cht-items-center cht-justify-between cht-border-b cht-border-gray-100 cht-pb-3">
                  <h3 className="cht-text-base cht-font-black cht-text-[#1e293b]">
                    {editingValueId ? "Edit Core Value" : "Add New Core Value"}
                  </h3>
                  <button
                    type="button"
                    onClick={() => setValueModalOpen(false)}
                    className="cht-text-gray-400 hover:cht-text-gray-600 cht-h-8 cht-w-8 cht-rounded-lg cht-flex cht-items-center cht-justify-center"
                  >
                    <X className="cht-h-4 cht-w-4" />
                  </button>
                </div>

                <form onSubmit={handleSaveCoreValue} className="cht-space-y-4 cht-text-xs">
                  <div>
                    <label className="cht-font-bold cht-text-gray-700 cht-block cht-mb-1">Value Title *</label>
                    <Input
                      type="text"
                      placeholder="e.g. Innovation & Excellence"
                      value={valueTitle}
                      onChange={(e) => setValueTitle(e.target.value)}
                      leftIcon={<Heart className="cht-h-4 cht-w-4 cht-text-gray-400" />}
                      required
                    />
                  </div>

                  <div>
                    <label className="cht-font-bold cht-text-gray-700 cht-block cht-mb-1">Description</label>
                    <Input
                      multiline
                      rows={3}
                      placeholder="Describe this core value principle..."
                      value={valueDescription}
                      onChange={(e) => setValueDescription(e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="cht-font-bold cht-text-gray-700 cht-block cht-mb-1">Value Icon / Graphic (Optional)</label>
                    <div className="cht-space-y-2">
                      {(valuePicUrl || valuePicFile) && (
                        <div className="cht-h-24 cht-w-full cht-bg-gray-100 cht-rounded-xl cht-overflow-hidden cht-border cht-border-gray-200 cht-relative cht-flex cht-items-center cht-justify-center">
                          <img
                            src={valuePicFile ? URL.createObjectURL(valuePicFile) : valuePicUrl}
                            alt="Value Preview"
                            className="cht-h-full cht-w-full cht-object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setValuePicFile(null);
                              setValuePicUrl("");
                              if (setValuePicMediaId) setValuePicMediaId(null);
                            }}
                            className="cht-absolute cht-top-2 cht-right-2 cht-h-6 cht-w-6 cht-bg-rose-600 cht-text-white cht-rounded-full cht-shadow-md cht-flex cht-items-center cht-justify-center hover:cht-bg-rose-700"
                          >
                            <X className="cht-h-3.5 cht-w-3.5" />
                          </button>
                        </div>
                      )}
                      <div>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={onOpenMediaModalForCoreValue}
                          leftIcon={<ImageIcon className="cht-h-4 cht-w-4 cht-text-[#3e57da]" />}
                          className="cht-w-full cht-text-xs cht-font-bold cht-rounded-xl"
                        >
                          Select from Media Library
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="cht-flex cht-items-center cht-justify-end cht-space-x-2 cht-pt-3 cht-border-t cht-border-gray-100">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setValueModalOpen(false)}
                      className="cht-text-xs cht-font-bold cht-rounded-xl"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={savingValue}
                      isLoading={savingValue}
                      className="cht-text-xs cht-font-bold cht-rounded-xl"
                    >
                      {savingValue ? "Saving..." : editingValueId ? "Update Value" : "Save Value"}
                    </Button>
                  </div>
                </form>
              </motion.div>
            </div>
          </AnimatePresence>,
          document.body
        )}
    </div>
  );
};
